/**
 * Error hygiene for clients.
 *
 * Rule: a message that reaches the browser must never leak server internals.
 * Only messages we deliberately mark as `PublicError` are shown verbatim;
 * every other thrown error is logged server-side and replaced with a generic message.
 *
 * The `sanitizeErrors` middleware is registered globally in `src/start.ts`.
 *
 * The underlying (non-public) error is forwarded to SigNoz/OTel from
 * `toClientError` so the full detail is preserved server-side without ever
 * reaching the client. See `src/lib/telemetry/telemetry.ts` and
 * `otel.server.ts` for the capture/export machinery.
 */
import { createMiddleware } from '@tanstack/react-start'

import { captureException } from '@/lib/telemetry/telemetry'

/** Shown to the client whenever an unexpected (non-public) error is caught. */
export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'

/**
 * An error whose message is safe to display to the end user. Throw this for
 * intentional, user-facing conditions (validation, auth, not-found). Anything
 * that is a plain `Error` is treated as internal and sanitized before it can
 * reach the client.
 */
export class PublicError extends Error {
  /** Suggested HTTP status for API-route callers; server fns ignore it. */
  readonly status: number

  constructor(message: string, status = 400) {
    super(message)
    this.name = 'PublicError'
    this.status = status
  }
}

/**
 * Map any thrown value to something safe to surface to the client.
 * - `PublicError` → returned as-is (its message is intentional).
 * - non-`Error` throwables (TanStack redirect/notFound control-flow, etc.) →
 *   returned untouched so framework behavior is preserved.
 * - any other `Error` → logged server-side and replaced with a generic message.
 */
export function toClientError(error: unknown): unknown {
  if (error instanceof PublicError) return error
  if (!(error instanceof Error)) return error
  // Real server-side failure — preserve the detail server-side (logs +
  // SigNoz/OTel), never on the wire to the client.
  console.error('[server]', error)
  captureException(error)
  return new PublicError(GENERIC_ERROR_MESSAGE, 500)
}

/**
 * Server-function middleware that sanitizes every error a handler throws.
 * Registered globally in `src/start.ts` — do not attach it per-fn.
 */
export const sanitizeErrors = createMiddleware({ type: 'function' }).server(
  async ({ next }) => {
    try {
      return await next()
    } catch (error) {
      throw toClientError(error)
    }
  },
)
